<template>
  <div
    :class="[dropdownStyles]"
  >
    <div ref="toggle" @click.capture="!manual && toggleDropdown()">
      <slot name="toggle" :is-open="opened" />
    </div>

    <transition name="fadeMaxHeight">
      <div
        v-if="opened"
        :class="bodyClasses"
        :style="{ top: `${topPosition}px` }"
      >
        <div
          :class="$style.content"
          ref="content"
        >
          <slot :max-height="calculatedMaxHeight"></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const DROPDOWN_MAX_HEIGHT = 400

export default {

  emits: [
    'close',
    'open',
    'toggle',
  ],
  props: {
    offsetTop: {
      type: Number,
      default: 12,
    },

    theme: {
      type: String,
      default: '',
    },

    short: {
      type: Boolean,
      default: false,
    },

    manual: {
      type: Boolean,
      default: false,
    },

    position: {
      type: Array,
      default: () => ['bottom', 'left'],
    },

    inline: {
      type: Boolean,
      default: false,
    },

    maxHeight: {
      type: Number,
      default: () => DROPDOWN_MAX_HEIGHT,
    },

    scrollIntoViewOptions: {
      type: Object,
      default: () => null,
    },
  },

  data () {
    return {
      opened: false,
      topPosition: 0,
      calculatedMaxHeight: null,
    }
  },

  computed: {
    dropdownStyles () {
      return [
        this.$style.dropdown,
        // !this.opened && this.$style.transition,
        this.short && this.$style.dropdowndefault,
        this.theme && this.$style[this.theme],
      ]
    },

    bodyClasses () {
      const position = this.position
      return [
        this.$style.body,
        position.length ? this.$style[`position-${position[0]}-${position[1]}`] : null,
        this.inline && this.$style.inline,
      ]
    },

  },
  methods: {
    toggleDropdown (event) {
      this.fitDropdown(event)
      if (this.disabled) return
      this.opened = !this.opened
      this.updateTopPosition()
      this.$emit('toggle', this.opened)
      // if (this.opened && !this.fixedPosition) this.adjustPosition()
      if (this.$props.scrollIntoViewOptions) {
        this.$nextTick(() => {
          this.opened && this.$refs.content?.scrollIntoView?.(this.$props.scrollIntoViewOptions)
        })
      }
    },

    open (callback) {
      if (!this.opened) {
        this.toggleDropdown()
      }
    },

    close (callback) {
      if (this.opened) {
        this.$el.style.marginBottom = ''
        this.toggleDropdown()
      }
    },

    updateTopPosition () {
      this.topPosition = this.$refs.toggle && this.position[0] === 'bottom'
        ? this.$refs.toggle.clientHeight + this.offsetTop
        : -this.offsetTop
    },

    windowClickHandler (event) {
      if (!document.body.contains(event.target) || this.$el.contains(document.activeElement)) return
      if (!this.$el.contains(event.target)) {
        this.toggleDropdown(event)
      }
    },

    keyUpHandler (event) {
      if (!document.body.contains(event.target) || this.$el.contains(document.activeElement)) return
      // if tab clicked
      if (event.keyCode === 9) {
        this.close()
      }
    },

    addWindowListeners () {
      window.addEventListener('click', this.windowClickHandler, { capture: true })
      window.addEventListener('keyup', this.keyUpHandler)
      window.addEventListener('touchend', this.windowClickHandler, { capture: true })
    },

    removeWindowListeners () {
      window.removeEventListener('click', this.windowClickHandler, { capture: true })
      window.addEventListener('keyup', this.keyUpHandler)
      window.removeEventListener('touchend', this.windowClickHandler, { capture: true })
    },

    fitDropdown (event) {
      if (!this.opened) {
        const relativeNode = getBoundrieElememt(this.$el)
        let offset
        if (relativeNode) {
          offset = (this.$el.clientHeight + this.$el.offsetTop)
          this.calculatedMaxHeight = this.maxHeight
        } else {
          this.calculatedMaxHeight = Math.min(window.innerHeight - (this.$el.getBoundingClientRect().bottom + 20), this.maxHeight)
        }
        this.$el.dispatchEvent(new CustomEvent('dropdown-open', {
          bubbles: true,
          detail: {
            height: this.calculatedMaxHeight,
            offset,
          },
        }))
      } else {
        this.calculatedMaxHeight = null
        this.$el.dispatchEvent(new CustomEvent('dropdown-close', {
          bubbles: true,
        }))
      }
    },

  },

  beforeUnmount () {
    this.removeWindowListeners()
  },

  watch: {
    opened (val) {
      if (val) {
        this.$emit('open')
        this.addWindowListeners()
      } else {
        this.$emit('close')
        this.removeWindowListeners()
      }
    },
  },
}

const getBoundrieElememt = (node) => {
  let parent = node.parentElement
  while (parent) {
    if (parent.dataset.boundries !== undefined && parent.dataset.boundries !== false) {
      return parent
    }
    parent = parent.parentElement
  }
  return parent
}
</script>

<style lang="scss" module>
.dropdown {
  position: relative;
  transition: .2s ease-in-out;
  transition-property: margin-bottom;
}
.body {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  min-width: 100%;

  &:before {
    content: '';
    width: 0;
    height: 0;
    position: absolute;
    top: -12px;
    left: 16px;
    border-style: solid;
    border-width: 0 10px 12px 10px;
    border-color: transparent transparent var(--color-white) transparent;
  }
}
.content {
  overflow: auto;
  box-shadow: $box-shadow-dropdown;
  border-radius: $border-radius;
  background-color: var(--color-white);
}
.small {
  .content {
    width: 100%;
    // max-height: 300px;
    padding: 0;
    overflow: auto;
  }
}

.position {
  &-bottom-center {
    &,
    &:before {
      left: 50%;
      transform: translateX(-50%);
    }
  }
  &-bottom-right {
    left: auto;
    right: 0;

    &:before {
      left: auto;
      right: 16px;
    }
  }
  &-top-center {
    transform: translate(-50%, -100%);

    &:before {
      top: auto;
      bottom: -12px;
      border-width: 12px 10px 0 10px;
      border-color: var(--color-white) transparent transparent transparent;
      left: 50%;
      transform: translate(-50%, 0);
    }
  }

  &-top-left {
    transform: translate(0, -100%);

    &:before {
      top: auto;
      bottom: -12px;
      border-width: 12px 10px 0 10px;
      border-color: var(--color-white) transparent transparent transparent;
      left: 16px;
      transform: translate(0, 0);
    }
  }
}

.inline {
  position: relative;
}
</style>
